import { FC } from 'react'
import { Button, Col, DatePicker, Divider, Form, Row, Select, Space, notification } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useForm } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import {
    requiredDatePickerRules,
    requiredRules,
} from '../../../../../../../constants/forms/validation/rules'
import { outputDateFormat } from '../../../../../../../constants/constants'
import { orderRequestType } from '../../../../../../../constants/entity-enums/order-request-types'
import { useCreateReceiptMutation } from '../../../../../../../store/api/receipts-api'
import { useCreateReceiptPositionMutation } from '../../../../../../../store/api/receipt-positions-api'
import {
    useCreateOrderRequestMutation,
    useUpdateOrderRequestMutation,
} from '../../../../../../../store/api/order-requests-api'
import { useCreateMaterialMutation } from '../../../../../../../store/api/materials-api'
import { useGetContractorsQuery } from '../../../../../../../store/api/contractors-api'
import { QueryParams } from '../../../../../../../types/strapi/strapi'
import { useUser } from '../../../../../../../hooks/use-user'
import useErrorModal from '../../../../../../../hooks/api/use-error-modal'
import { resolveMaterialDocumentId } from '../../../../../material-order-requests/components/material-order-requests/helpers/resolve-material-document-id'
import { useAppDispatch } from '../../../../../../../store/store'
import { stocksApi } from '../../../../../../../store/api/stocks-api'
import { buildMaterialWidthOptions } from './helpers/build-material-width-options'
import ReceiptPositionFields from './components/receipt-position-fields'
import CreateReceiptFormValues from './types/form-values'

const contractorsParams: QueryParams = {
    filters: { type: { name: { $contains: 'Поставщик материала' } } },
}

interface CreateReceiptFormProps {
    onSuccess: () => void
}

const CreateReceiptForm: FC<CreateReceiptFormProps> = ({ onSuccess }) => {
    const [form] = useForm<CreateReceiptFormValues>()
    const user = useUser()
    const dispatch = useAppDispatch()
    const [createReceipt, createReceiptState] = useCreateReceiptMutation()
    const [createReceiptPosition, createReceiptPositionState] = useCreateReceiptPositionMutation()
    const [createOrderRequest, createOrderRequestState] = useCreateOrderRequestMutation()
    const [updateOrderRequest, updateOrderRequestState] = useUpdateOrderRequestMutation()
    const [createMaterial, createMaterialState] = useCreateMaterialMutation()
    const { data: contractors } = useGetContractorsQuery(contractorsParams)

    useErrorModal(createReceiptState, 'Ошибка создания заказа')
    useErrorModal(createReceiptPositionState, 'Ошибка создания позиции заказа')
    useErrorModal(createOrderRequestState, 'Ошибка создания заявки')
    useErrorModal(updateOrderRequestState, 'Ошибка обновления заявки')
    useErrorModal(createMaterialState, 'Ошибка создания материала')

    const isSaving =
        createReceiptState.isLoading ||
        createReceiptPositionState.isLoading ||
        createOrderRequestState.isLoading ||
        updateOrderRequestState.isLoading ||
        createMaterialState.isLoading

    const onSave = async () => {
        try {
            const values = await form.validateFields()
            const receiptPositionIds: string[] = []
            const orderRequestDocIds: string[] = []

            for (const position of values.positions) {
                if (!position.unitDocumentId) {
                    notification.error({
                        message: 'Не удалось определить единицу измерения для позиции',
                    })
                    return
                }

                const stocksResult = await dispatch(
                    stocksApi.endpoints.getStocks.initiate({
                        filters: {
                            material: {
                                material_type: { documentId: { $eq: position.materialTypeDocId } },
                            },
                        },
                        populate: '*',
                        pagination: { page: 1, pageSize: 1000 },
                    })
                ).unwrap()

                const stocks = stocksResult.data || []
                const materials = stocks.map(item => item.material)
                const materialOptions = buildMaterialWidthOptions(stocks)

                const materialDocumentId = await resolveMaterialDocumentId({
                    materialTypeDocId: position.materialTypeDocId,
                    materialValue: position.material,
                    createMaterial: body => createMaterial(body).then(res => ({ data: res.data })),
                    materials,
                    materialOptions,
                })

                if (!materialDocumentId) {
                    return
                }

                const orderRequestResult = await createOrderRequest({
                    data: {
                        goodsType: orderRequestType[0],
                        requestStatus: 'Новый',
                        amount: position.amount,
                        initiator: { connect: [position.initiator] },
                        material: { connect: [materialDocumentId] },
                        unit: { connect: [position.unitDocumentId] },
                    },
                }).unwrap()

                const orderRequestDocId = orderRequestResult.data?.documentId
                if (!orderRequestDocId) {
                    return
                }

                orderRequestDocIds.push(orderRequestDocId)

                const positionResult = await createReceiptPosition({
                    data: {
                        positionType: 'Материал',
                        unit: { connect: [position.unitDocumentId] },
                        amount: position.amount,
                        material: { connect: [materialDocumentId] },
                        initiator: { connect: [position.initiator] },
                        order_request: { connect: [orderRequestDocId] },
                    },
                }).unwrap()

                if (positionResult.data?.documentId) {
                    receiptPositionIds.push(positionResult.data.documentId)
                }
            }

            if (receiptPositionIds.length === 0) {
                notification.error({ message: 'Не удалось создать позиции заказа' })
                return
            }

            await createReceipt({
                data: {
                    receiptStatus: 'Создан',
                    supplier: { connect: [values.supplier] },
                    receipt_positions: { connect: receiptPositionIds },
                    estimatedDate: values.estimatedDate.toISOString(),
                },
            }).unwrap()

            await Promise.all(
                orderRequestDocIds.map(docId =>
                    updateOrderRequest({
                        docId,
                        body: { data: { requestStatus: 'Обработан' } },
                    }).unwrap()
                )
            )

            notification.success({ message: 'Заказ создан' })
            form.resetFields()
            form.setFieldsValue({
                estimatedDate: dayjs().add(3, 'day'),
                positions: user?.documentId ? [{ initiator: user.documentId }] : [{}],
            })
            onSuccess()
        } catch (error) {
            notification.error({ message: 'Ошибка создания заказа' })
            console.error(error)
        }
    }

    return (
        <Form
            form={form}
            layout={'vertical'}
            initialValues={{
                estimatedDate: dayjs().add(3, 'day'),
                positions: user?.documentId ? [{ initiator: user.documentId }] : [{}],
            }}
        >
            <Row gutter={12}>
                <Col sm={6}>
                    <Form.Item name={'supplier'} label={'Поставщик'} rules={requiredRules}>
                        <Select
                            showSearch
                            optionFilterProp='label'
                            options={contractors?.data.map(x => ({
                                label: x.name,
                                value: x.documentId,
                            }))}
                        />
                    </Form.Item>
                </Col>
                <Col sm={6}>
                    <Form.Item
                        name={'estimatedDate'}
                        label={'Дата поставки'}
                        rules={requiredDatePickerRules}
                    >
                        <DatePicker format={outputDateFormat} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation={'left'}>Позиции заказа</Divider>

            <Form.List
                name={'positions'}
                rules={[
                    {
                        validator: async (_, positions) => {
                            if (!positions || positions.length < 1) {
                                return Promise.reject(new Error('Добавьте хотя бы одну позицию'))
                            }
                        },
                    },
                ]}
            >
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(field => (
                            <Row key={field.key} gutter={12}>
                                <ReceiptPositionFields name={field.name} />
                                {fields.length > 1 && (
                                    <Form.Item label={' '}>
                                        <Button
                                            type={'link'}
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => remove(field.name)}
                                            style={{ marginBottom: 16 }}
                                        >
                                            Удалить позицию
                                        </Button>
                                    </Form.Item>
                                )}
                            </Row>
                        ))}
                        <Form.Item>
                            <Button
                                type={'dashed'}
                                onClick={() => add({ initiator: user?.documentId })}
                                block
                                icon={<PlusOutlined />}
                            >
                                Добавить позицию
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List>

            <Space>
                <Button type={'primary'} onClick={onSave} loading={isSaving}>
                    Создать заказ
                </Button>
            </Space>
        </Form>
    )
}

export default CreateReceiptForm
